import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResourceCard = ({ resource, onDownload, onView }) => {
  const getTypeIcon = (type) => {
    switch (type) {
      case 'whitepaper': return 'FileText';
      case 'calculator': return 'Calculator';
      case 'video': return 'Play';
      case 'webinar': return 'Video';
      case 'guide': return 'BookOpen';
      case 'assessment': return 'CheckSquare';
      default: return 'File';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'whitepaper': return 'text-blue-600 bg-blue-50';
      case 'calculator': return 'text-green-600 bg-green-50';
      case 'video': return 'text-purple-600 bg-purple-50';
      case 'webinar': return 'text-orange-600 bg-orange-50';
      case 'guide': return 'text-indigo-600 bg-indigo-50';
      case 'assessment': return 'text-cyan-600 bg-cyan-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'C-Suite': return 'bg-red-100 text-red-800';
      case 'Technical': return 'bg-blue-100 text-blue-800';
      case 'Procurement': return 'bg-green-100 text-green-800';
      case 'Business': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-executive border border-gray-200 hover:shadow-executive-lg transition-all duration-300 group">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-lg ${getTypeColor(resource?.type)}`}>
            <Icon name={getTypeIcon(resource?.type)} size={24} />
          </div>
          {resource?.featured && (
            <span className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-3 py-1 rounded-full text-xs font-medium">
              Featured
            </span>
          )}
        </div>

        {/* Content */}
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-text-primary mb-2 group-hover:text-brand-primary transition-colors duration-200">
            {resource?.title}
          </h3>
          <p className="text-text-secondary text-sm mb-3 line-clamp-3">
            {resource?.description}
          </p>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(resource?.targetRole)}`}>
              {resource?.targetRole}
            </span>
            {resource?.industry && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                {resource?.industry}
              </span>
            )}
          </div>
        </div>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-sm text-text-secondary mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={16} />
              <span>{resource?.readTime}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Download" size={16} />
              <span>{resource?.downloads}</span>
            </div>
          </div>
          <span className="text-xs">{resource?.publishDate}</span>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          {resource?.type === 'calculator' || resource?.type === 'assessment' ? (
            <Button
              variant="default"
              fullWidth
              className="bg-brand-primary hover:bg-brand-primary/90"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => onView(resource)}
            >
              Launch Tool
            </Button>
          ) : resource?.type === 'video' || resource?.type === 'webinar' ? (
            <Button
              variant="default"
              fullWidth
              className="bg-brand-primary hover:bg-brand-primary/90"
              iconName="Play"
              iconPosition="right"
              onClick={() => onView(resource)}
            >
              Watch Now
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                className="flex-1"
                iconName="Eye"
                iconPosition="left"
                onClick={() => onView(resource)}
              >
                Preview
              </Button>
              <Button
                variant="default"
                className="flex-1 bg-brand-primary hover:bg-brand-primary/90"
                iconName="Download"
                iconPosition="right"
                onClick={() => onDownload(resource)}
              >
                Download
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;